import { Component, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyService } from 'src/app/services/currency.service';
import { CancelMembershipRequest, CancellationPreview } from 'src/app/models/currency';

type CancelStep = 'loading' | 'consequences' | 'reason' | 'confirm' | 'success' | 'error';

@Component({
  selector: 'app-cancel-membership',
  templateUrl: './cancel-membership.component.html',
  styleUrls: ['./cancel-membership.component.scss']
})
export class CancelMembershipComponent implements OnInit {

  step: CancelStep = 'loading';
  preview: CancellationPreview | null = null;
  errorMessage = '';
  isLoading = false;

  memberId = 0;
  bookingId = 0;
  packTitle = '';

  selectedReasonCategory = '';
  reasonText = '';
  readonly additionalDetailsMaxLength = 255;

  readonly reasonCategories = [
    'Financial reasons',
    'Moving to a new location',
    'Health/injury concerns',
    'Not enough time to use',
    'Unsatisfied with facilities',
    'Unsatisfied with the classes',
    'Temporary break needed',
    'Other reason'
  ];

  private readonly terminalScenarios = new Set([
    'alreadyCancelRequested',
    'alreadyCancelled',
    'freeTrial'
  ]);

  constructor(
    public activeModal: NgbActiveModal,
    private currencyService: CurrencyService
  ) {}

  ngOnInit(): void {
    this.loadPreview();
  }

  get isTerminal(): boolean {
    return !!this.preview && this.terminalScenarios.has(this.preview.Scenario);
  }

  get heroHeading(): string {
    if (!this.preview) {
      return '';
    }
    return this.isTerminal ? this.preview.Headline : 'Are you sure you want to cancel?';
  }

  get canProceed(): boolean {
    return !!this.preview?.Eligibility?.CanCancel && !this.isTerminal;
  }

  get showEligibilityBlock(): boolean {
    const eligibility = this.preview?.Eligibility;
    return !!eligibility && !eligibility.CanCancel;
  }

  get submitButtonLabel(): string {
    return this.preview?.SubmitButtonLabel || 'Continue';
  }

  get formattedEarliestEligibleDate(): string | null {
    const date = this.preview?.Eligibility?.EarliestEligibleDate;
    if (!date) {
      return null;
    }
    const parsed = new Date(`${date}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
      return date;
    }
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get canContinueFromReason(): boolean {
    return this.selectedReasonCategory.length > 0;
  }

  get additionalDetailsLength(): number {
    return this.reasonText.length;
  }

  get isAdditionalDetailsTooLong(): boolean {
    return this.reasonText.trim().length > this.additionalDetailsMaxLength;
  }

  get canSubmit(): boolean {
    if (this.isAdditionalDetailsTooLong) {
      return false;
    }
    if (this.selectedReasonCategory === 'Other reason') {
      return this.reasonText.trim().length > 0;
    }
    return true;
  }

  get showFooter(): boolean {
    return ['consequences', 'reason', 'confirm', 'success'].includes(this.step);
  }

  loadPreview(): void {
    this.step = 'loading';
    this.currencyService.getCancellationPreview(this.bookingId).subscribe({
      next: (preview) => {
        this.preview = preview;
        this.step = 'consequences';
      },
      error: (err) => {
        this.errorMessage = this.extractErrorMessage(err, 'Failed to load cancellation details. Please try again later.');
        this.step = 'error';
      }
    });
  }

  goToReason(): void {
    if (this.canProceed) {
      this.step = 'reason';
    }
  }

  goToConfirm(): void {
    if (this.canContinueFromReason) {
      this.errorMessage = '';
      this.step = 'confirm';
    }
  }

  goBackToConsequences(): void {
    this.step = 'consequences';
  }

  goBackToReason(): void {
    this.step = 'reason';
  }

  onKeepMembership(): void {
    this.activeModal.dismiss(false);
  }

  onSubmit(): void {
    if (this.isAdditionalDetailsTooLong) {
      this.errorMessage = `Additional details must be ${this.additionalDetailsMaxLength} characters or fewer.`;
      return;
    }

    if (!this.canSubmit || !this.preview) {
      return;
    }

    this.isLoading = true;

    const cancelRequest: CancelMembershipRequest = {
      MemberId: this.memberId,
      BookingId: this.bookingId,
      ReasonCategory: this.selectedReasonCategory,
      Reason: this.reasonText.trim()
    };

    this.currencyService.cancelSubscription(cancelRequest).subscribe({
      next: () => {
        this.isLoading = false;
        this.step = 'success';
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = this.extractErrorMessage(err, 'Failed to submit cancellation request. Please try again later.');
        console.error(err);
      }
    });
  }

  onDone(): void {
    this.activeModal.close(true);
  }

  selectReasonCategory(category: string): void {
    this.selectedReasonCategory = category;
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const serverMsg = body.message ?? body.Message ?? body.detail ?? body.title
        ?? body.Eligibility?.Message ?? body.eligibility?.message;
      if (typeof serverMsg === 'string' && serverMsg.trim()) {
        return serverMsg;
      }
    }
    return fallback;
  }
}
